'use strict';

/**
 * Macro Calculator — Web Component
 * Pure calculation engine + clean DOM binding.
 * No external dependencies. No global scope pollution.
 */
class MacroCalculator extends HTMLElement {
  /** @type {Record<string, unknown>} */
  #config = {};

  connectedCallback() {
    try {
      this.#config = JSON.parse(this.dataset.config || '{}');
    } catch {
      this.#config = {};
    }
    this.#initSliders();
    this.#initUnitToggles();
    this.#bindFormEvents();
  }

  /* ─────────────────────────────────────────────
     Slider initialisation
     ───────────────────────────────────────────── */
  #initSliders() {
    this.querySelectorAll('[data-mc-slider]').forEach((slider) => {
      this.#syncSlider(slider);
      slider.addEventListener('input', () => this.#syncSlider(slider));
    });
  }

  #syncSlider(slider) {
    const min = Number(slider.min) || 0;
    const max = Number(slider.max) || 100;
    const val = Number(slider.value);
    const pct = ((val - min) / (max - min)) * 100;

    // CSS fill behind thumb
    slider.style.setProperty('--fill-pct', `${pct}%`);

    // Floating bubble
    const key = slider.dataset.mcSlider;
    const bubble = this.querySelector(`[data-mc-slider-bubble="${key}"]`);
    if (bubble) {
      bubble.textContent = val;
      // Position: percentage of slider width accounting for thumb radius (~11px)
      bubble.style.left = `calc(${pct}% + (${11 - pct * 0.22}px))`;
    }

    // aria
    slider.setAttribute('aria-valuenow', String(val));
  }

  /* ─────────────────────────────────────────────
     Unit toggle — convert existing value on change
     ───────────────────────────────────────────── */
  #initUnitToggles() {
    this.#initHeightAutoFocus();
    this.#watchUnitToggle('weight-unit', 'current-weight', (val, toUnit) =>
      toUnit === 'kg'
        ? Math.round((val / 2.2046) * 10) / 10
        : Math.round(val * 2.2046)
    );
    this.#watchUnitToggle('goal-weight-unit', 'goal-weight', (val, toUnit) =>
      toUnit === 'kg'
        ? Math.round((val / 2.2046) * 10) / 10
        : Math.round(val * 2.2046)
    );
  }

  #watchUnitToggle(radioName, inputKey, converter) {
    this.querySelectorAll(`[name="${radioName}"]`).forEach((radio) => {
      radio.addEventListener('change', () => {
        const input = this.querySelector(`[data-mc-input="${inputKey}"]`);
        if (!input) return;
        const current = parseFloat(input.value);
        if (!isNaN(current) && current > 0) {
          input.value = converter(current, radio.value);
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
     Height auto-focus — OTP-style ft → in
     ───────────────────────────────────────────── */
  #initHeightAutoFocus() {
    const ftInput = this.querySelector('[data-mc-input="height-ft"]');
    const inInput = this.querySelector('[data-mc-input="height-in"]');
    if (!ftInput || !inInput) return;

    ftInput.addEventListener('input', () => {
      const val = ftInput.value;
      if (val.length >= 1 && Number(val) >= 3) {
        inInput.focus();
        inInput.select();
      }
    });
  }

  /* ─────────────────────────────────────────────
     Form events
     ───────────────────────────────────────────── */
  #bindFormEvents() {
    const form = this.querySelector('[data-mc-form]');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#onCalculate();
    });

    // Clear field error on input (after first submit attempt)
    form.addEventListener(
      'input',
      (e) => {
        let key = e.target.dataset?.mcInput;
        if (key?.startsWith('height-')) key = 'height';
        if (key) this.#clearFieldError(key);
        this.#checkFormReady();
      },
      { passive: true }
    );

    form.addEventListener(
      'change',
      (e) => {
        const key = e.target.dataset?.mcInput || e.target.name;
        if (key) this.#clearFieldError(key);
        this.#checkFormReady();
      },
      { passive: true }
    );

    // Set initial button state
    this.#checkFormReady();
  }

  /* ─────────────────────────────────────────────
     Button enable/disable based on form completeness
     ───────────────────────────────────────────── */
  #checkFormReady() {
    const btn = this.querySelector('[data-mc-calculate]');
    if (!btn) return;

    const getRadio = (name) =>
      !!this.querySelector(`[name="${name}"]:checked`);
    const getNum = (key) => {
      const val = parseFloat(this.querySelector(`[data-mc-input="${key}"]`)?.value);
      return !isNaN(val) && val > 0;
    };
    const disclaimerChecked =
      this.querySelector('[data-mc-disclaimer]')?.checked ?? false;
    const emailFilled =
      !!this.querySelector('[data-mc-input="email"]')?.value?.trim();

    const ready =
      getRadio('goal') &&
      getRadio('lifestyle') &&
      getRadio('workout-style') &&
      getRadio('diet') &&
      getRadio('gender') &&
      getNum('age') &&
      getNum('height-ft') &&
      getNum('current-weight') &&
      getNum('goal-weight') &&
      emailFilled &&
      disclaimerChecked;

    btn.disabled = !ready;
  }

  /* ─────────────────────────────────────────────
     Main calculate handler
     ───────────────────────────────────────────── */
  #onCalculate() {
    const raw = this.#collectInputs();
    const normalized = this.#normalizeUnits(raw);
    const errors = this.#validate(normalized);

    this.#clearAllErrors();

    if (Object.keys(errors).length > 0) {
      this.#showErrors(errors);
      return;
    }

    const result = this.#compute(normalized);
    this.#renderResult(result);
    this.#showProductRecommendations(result.goal, result.diet);
    this.#revealResults();
    this.#klaviyoTrack(raw, result);
    this.#showToast('Your results have been emailed to ' + raw.email);
  }

  /* ─────────────────────────────────────────────
     Toast notification
     ───────────────────────────────────────────── */
  #showToast(message) {
    let toast = document.querySelector('.mc-toast');
    if (toast) toast.remove();

    toast = document.createElement('div');
    toast.className = 'mc-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <svg class="mc-toast-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
      <span>${message}</span>
    `;
    document.body.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('mc-toast--show'));

    setTimeout(() => {
      toast.classList.remove('mc-toast--show');
      setTimeout(() => toast.remove(), 400);
    }, 4000);
  }

  /* ─────────────────────────────────────────────
     Input collection
     ───────────────────────────────────────────── */
  #collectInputs() {
    const getRadio = (name) =>
      this.querySelector(`[name="${name}"]:checked`)?.value ?? null;
    const getNum = (key) => {
      const raw = this.querySelector(`[data-mc-input="${key}"]`)?.value;
      return raw ? parseFloat(raw) : undefined;
    };
    const getSlider = (key) =>
      parseFloat(this.querySelector(`[data-mc-slider="${key}"]`)?.value ?? 0);

    return {
      goal: getRadio('goal'),
      lifestyle: getRadio('lifestyle'),
      workoutStyle: getRadio('workout-style'),
      diet: getRadio('diet'),
      gender: getRadio('gender'),
      weightUnit: getRadio('weight-unit') || 'kg',
      goalWeightUnit: getRadio('goal-weight-unit') || 'kg',
      age: getNum('age'),
      heightFt: getNum('height-ft'),
      heightIn: getNum('height-in'),
      currentWeight: getNum('current-weight'),
      targetWeight: getNum('goal-weight'),
      workoutDays: getSlider('days'),
      workoutMins: getSlider('mins'),
      disclaimerAccepted:
        this.querySelector('[data-mc-disclaimer]')?.checked ?? false,
      marketingAccepted:
        this.querySelector('[name="marketing"]')?.checked ?? false,
      email:
        this.querySelector('[data-mc-input="email"]')?.value?.trim() ?? '',
    };
  }

  /* ─────────────────────────────────────────────
     Unit normalisation — always to kg + cm
     ───────────────────────────────────────────── */
  #normalizeUnits(inputs) {
    const heightCm =
      (((inputs.heightFt ?? 0) * 12) + (inputs.heightIn ?? 0)) * 2.54;
    const currentWeightKg =
      inputs.weightUnit === 'lbs'
        ? (inputs.currentWeight ?? 0) / 2.2046
        : (inputs.currentWeight ?? 0);
    const targetWeightKg =
      inputs.goalWeightUnit === 'lbs'
        ? (inputs.targetWeight ?? 0) / 2.2046
        : (inputs.targetWeight ?? 0);

    return { ...inputs, heightCm, currentWeightKg, targetWeightKg };
  }

  /* ─────────────────────────────────────────────
     Validation — returns { field: message }
     ───────────────────────────────────────────── */
  #validate(inputs) {
    const errors = {};
    const { weightUnit, goalWeightUnit } = inputs;

    if (!inputs.goal) errors.goal = 'Please select a goal.';
    if (!inputs.lifestyle) errors.lifestyle = 'Please select your lifestyle.';
    if (!inputs.workoutStyle)
      errors['workout-style'] = 'Please select a workout style.';
    if (!inputs.diet) errors.diet = 'Please select a diet.';
    if (!inputs.gender) errors.gender = 'Please select a gender.';

    if (
      !inputs.age ||
      isNaN(inputs.age) ||
      inputs.age < 13 ||
      inputs.age > 100
    ) {
      errors.age = 'Please enter an age between 13 and 100.';
    }

    if (
      !inputs.heightCm ||
      isNaN(inputs.heightCm) ||
      inputs.heightCm < 122 ||
      inputs.heightCm > 244
    ) {
      errors.height = 'Please enter a valid height (4\'0″ – 8\'0″).';
    }

    const weightMin = weightUnit === 'lbs' ? 30 : 14;
    const weightMax = weightUnit === 'lbs' ? 500 : 227;
    if (
      !inputs.currentWeightKg ||
      isNaN(inputs.currentWeightKg) ||
      inputs.currentWeightKg < 14 ||
      inputs.currentWeightKg > 227
    ) {
      errors['current-weight'] = `Please enter a valid weight (${weightMin}–${weightMax} ${weightUnit}).`;
    }

    const gwMin = goalWeightUnit === 'lbs' ? 30 : 14;
    const gwMax = goalWeightUnit === 'lbs' ? 500 : 227;
    if (
      !inputs.targetWeightKg ||
      isNaN(inputs.targetWeightKg) ||
      inputs.targetWeightKg < 14 ||
      inputs.targetWeightKg > 227
    ) {
      errors['goal-weight'] = `Please enter a valid target weight (${gwMin}–${gwMax} ${goalWeightUnit}).`;
    }

    // Weight direction vs goal — only check if both weights are individually valid
    if (!errors['current-weight'] && !errors['goal-weight']) {
      if (
        ['lose-weight', 'get-shredded'].includes(inputs.goal) &&
        inputs.targetWeightKg >= inputs.currentWeightKg
      ) {
        errors['goal-weight'] =
          'Target weight must be lower than current weight for this goal.';
      }
      if (
        inputs.goal === 'lean-bulk' &&
        inputs.targetWeightKg <= inputs.currentWeightKg
      ) {
        errors['goal-weight'] =
          'Target weight must be higher than current weight for this goal.';
      }
    }

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!inputs.email || !emailRe.test(inputs.email)) {
      errors.email = 'Please enter a valid email address.';
    }

    if (!inputs.disclaimerAccepted) {
      errors.disclaimer = 'Please accept the disclaimer to continue.';
    }

    return errors;
  }

  /* ─────────────────────────────────────────────
     Core computation — pure, no DOM access
     ───────────────────────────────────────────── */
  #compute(inputs) {
    const warnings = [];

    // ── BMR: Mifflin-St Jeor ──────────────────
    let bmr =
      10 * inputs.currentWeightKg +
      6.25 * inputs.heightCm -
      5 * inputs.age;
    if (inputs.gender === 'male') bmr += 5;
    else if (inputs.gender === 'female') bmr -= 161;
    else bmr -= 78; // 'other': average constant ((5 + -161) / 2)

    // ── Lifestyle multiplier (TDEE) ───────────
    const lifestyleMultipliers = {
      sedentary: 1.2,
      'light-activity': 1.375,
      active: 1.55,
      'very-active': 1.725,
    };
    let tdee = bmr * (lifestyleMultipliers[inputs.lifestyle] ?? 1.2);

    // ── Workout bonus (MET-based, added to lifestyle TDEE) ───
    // MET values: light=4, moderate=6, heavy=8 (kcal/kg/hr)
    // Extra calories = MET × weight × hours per session × sessions per week ÷ 7
    const metValues = { light: 4, moderate: 6, heavy: 8 };
    const met = metValues[inputs.workoutStyle] ?? 0;
    if (met > 0 && inputs.workoutDays > 0 && inputs.workoutMins > 0) {
      const sessionKcal = met * inputs.currentWeightKg * (inputs.workoutMins / 60);
      tdee += (sessionKcal * inputs.workoutDays) / 7;
    }

    tdee = Math.round(tdee);

    // ── Goal calorie adjustment ───────────────
    const goalMultipliers = {
      'lose-weight': 0.8,
      'get-shredded': 0.8,
      'body-recomposition': 0.9,
      'lean-bulk': 1.1,
      maintenance: 1.0,
    };
    let targetCalories = Math.floor(tdee * (goalMultipliers[inputs.goal] ?? 1.0));

    // ── Calorie floor ─────────────────────────
    const floors = {
      male: Number(this.#config.calorie_floor_male) || 1500,
      female: Number(this.#config.calorie_floor_female) || 1200,
      other: 1350,
    };
    const floor = floors[inputs.gender] ?? 1200;
    let calorieClamped = false;
    if (targetCalories < floor) {
      targetCalories = floor;
      calorieClamped = true;
      warnings.push(
        `Your calculated calories fell below the recommended minimum of ${floor} kcal/day. Adjusted for safety.`
      );
    }

    // ── Macro percentages ─────────────────────
    const macros = this.#getMacroPercentages(inputs.diet);

    // ── Macro grams — carbs absorbs rounding remainder ──
    const { proteinG, carbsG, fatG } = this.#reconcileMacros(
      targetCalories,
      macros.protein,
      macros.carbs,
      macros.fat
    );

    return {
      goal: inputs.goal,
      diet: inputs.diet,
      gender: inputs.gender,
      tdee,
      targetCalories,
      calorieClamped,
      macros,
      macroGrams: { proteinG, carbsG, fatG },
      goalLabel: this.#getGoalLabel(inputs.goal),
      dietLabel: this.#getDietLabel(inputs.diet),
      warnings,
    };
  }

  /* ─────────────────────────────────────────────
     Macro percentages — corrected from source bugs
     ───────────────────────────────────────────── */
  #getMacroPercentages(diet) {
    const presets = {
      'low-carb': { protein: 0.5, carbs: 0.2, fat: 0.3 },
      keto: { protein: 0.35, carbs: 0.05, fat: 0.6 },
      'the-zone': { protein: 0.3, carbs: 0.4, fat: 0.3 },
      vegan: { protein: 0.3, carbs: 0.4, fat: 0.3 },
      balanced: { protein: 0.25, carbs: 0.5, fat: 0.25 },
    };
    return presets[diet] ?? presets.balanced;
  }

  /* ─────────────────────────────────────────────
     Macro reconciliation — locks protein + fat,
     carbs absorbs any rounding remainder
     ───────────────────────────────────────────── */
  #reconcileMacros(calories, proteinPct, carbsPct, fatPct) {
    const proteinG = Math.round((proteinPct * calories) / 4);
    const fatG = Math.round((fatPct * calories) / 9);
    const remaining = calories - proteinG * 4 - fatG * 9;
    const carbsG = Math.round(Math.max(0, remaining / 4));
    return { proteinG, carbsG, fatG };
  }

  /* ─────────────────────────────────────────────
     Time to goal (1 kg ≈ 7700 kcal)
     ───────────────────────────────────────────── */
  /* ─────────────────────────────────────────────
     Label helpers
     ───────────────────────────────────────────── */
  #getGoalLabel(goal) {
    return (
      {
        'lose-weight': 'Lose Weight',
        'get-shredded': 'Get Shredded',
        'body-recomposition': 'Body Recomposition',
        'lean-bulk': 'Lean Bulk',
        maintenance: 'Maintenance',
      }[goal] ?? goal
    );
  }

  #getDietLabel(diet) {
    return (
      {
        'low-carb': 'Low Carb / High Protein',
        keto: 'Keto',
        'the-zone': 'The Zone',
        vegan: 'Vegan',
        balanced: 'Balanced / Average',
      }[diet] ?? diet
    );
  }

  /* ─────────────────────────────────────────────
     Render result to DOM — only DOM access point
     ───────────────────────────────────────────── */
  #renderResult(result) {
    const set = (key, text) => {
      const el = this.querySelector(`[data-output="${key}"]`);
      if (el) el.textContent = text;
    };

    set('tdee', result.tdee.toLocaleString());
    set('calories', result.targetCalories.toLocaleString());
    set('goal-label', result.goalLabel);

    const { protein, carbs, fat } = result.macros;
    set(
      'diet-label',
      `${result.dietLabel} (${Math.round(protein * 100)} : ${Math.round(carbs * 100)} : ${Math.round(fat * 100)})`
    );

    set('protein-pct', `${Math.round(protein * 100)}%`);
    set('protein-g', `${result.macroGrams.proteinG}g`);
    set('carbs-pct', `${Math.round(carbs * 100)}%`);
    set('carbs-g', `${result.macroGrams.carbsG}g`);
    set('fat-pct', `${Math.round(fat * 100)}%`);
    set('fat-g', `${result.macroGrams.fatG}g`);

    // Warnings
    const warningsEl = this.querySelector('[data-mc-warnings]');
    if (warningsEl) {
      if (result.warnings.length > 0) {
        warningsEl.innerHTML = result.warnings
          .map((w) => `<p>${w}</p>`)
          .join('');
        warningsEl.removeAttribute('hidden');
      } else {
        warningsEl.setAttribute('hidden', '');
      }
    }
  }

  /* ─────────────────────────────────────────────
     Product recommendations visibility
     ───────────────────────────────────────────── */
  #showProductRecommendations(goal, diet) {
    const container = this.querySelector('[data-mc-products-container]');
    if (!container) return;

    // Hide all sections first
    this.querySelectorAll('[data-mc-products]').forEach((el) =>
      el.setAttribute('hidden', '')
    );

    // Lookup priority: goal+diet → goal+any → goal (legacy fallback)
    const keys = [`${goal}--${diet}`, `${goal}--any`, goal];
    let target = null;
    for (const key of keys) {
      target = this.querySelector(`[data-mc-products="${key}"]`);
      if (target) break;
    }

    if (target) {
      container.removeAttribute('hidden');
      target.removeAttribute('hidden');
    } else {
      container.setAttribute('hidden', '');
    }
  }

  /* ─────────────────────────────────────────────
     Reveal results + scroll + focus
     ───────────────────────────────────────────── */
  #revealResults() {
    const results = this.querySelector('[data-mc-results]');
    if (!results) return;

    results.removeAttribute('hidden');

    requestAnimationFrame(() => {
      results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Move focus for screen readers — heading has tabindex="-1"
      const heading = results.querySelector('[data-mc-results-heading]');
      heading?.focus({ preventScroll: true });
    });
  }

  /* ─────────────────────────────────────────────
     Klaviyo — identify, track event, subscribe
     ───────────────────────────────────────────── */
  #klaviyoTrack(raw, result) {
    const learnq = window._learnq;
    if (!learnq) return;

    const shopUrl = this.#config.shop_url || window.location.origin;

    const productMap = {
      'lose-weight': [
        { name: 'Caloric Burn X',       handle: 'caloric-burn-x' },
        { name: 'CLA 1X',               handle: 'cla' },
        { name: 'Stim Free Fat Burner', handle: 'stim-free-fat-burner' },
        { name: 'Complete Greens',      handle: 'complete-greens' },
      ],
      'get-shredded': [
        { name: 'Caloric Burn X',            handle: 'caloric-burn-x' },
        { name: 'NOCTHERMO',                 handle: 'nocthermo' },
        { name: 'Night Shred Pack',          handle: 'night-shred-pack' },
        { name: 'Creaflow',                  handle: 'creaflow-creatine-pump-capsules' },
      ],
      'body-recomposition': [
        { name: 'Isolate Protein',        handle: 'premium-whey-isolate-protein' },
        { name: 'Creatine Monohydrate',   handle: 'creatine-monohydrate' },
        { name: 'EAA + Hydration Aminos', handle: 'energized-aminos' },
        { name: 'Creatine + Aminos Bundle', handle: 'creatine-aminos-bundle' },
      ],
      'lean-bulk': [
        { name: 'Isolate Protein',        handle: 'premium-whey-isolate-protein' },
        { name: 'Creatine Monohydrate',   handle: 'creatine-monohydrate' },
        { name: 'Decipher Preworkout',    handle: 'decipher-high-stim-preworkout' },
        { name: 'PCT + Test',             handle: 'pct-test' },
      ],
      maintenance: [
        { name: 'Essenti+ Vitamin Pack',  handle: 'essenti-vitamin-pack' },
        { name: 'Advanced Magnesium',     handle: 'advanced-magnesium' },
        { name: 'Omega 3 Fish Oil',       handle: 'omega-3-fish-oil-lgxnds' },
        { name: 'Ashwagandha',            handle: 'ashwagandha' },
      ],
    };

    const products = (productMap[result.goal] || []).map((p) => ({
      name: p.name,
      url: `${shopUrl}/products/${p.handle}`,
      image_url: `${shopUrl}/products/${p.handle}`,
    }));

    // 1 — Identify profile
    learnq.push(['identify', { $email: raw.email }]);

    // 2 — Track event with full macro results
    learnq.push([
      'track',
      'Macro Calculator Completed',
      {
        email:          raw.email,
        goal:           result.goalLabel,
        diet:           result.dietLabel,
        tdee:           result.tdee,
        calories:       result.targetCalories,
        protein_pct:    `${Math.round(result.macros.protein * 100)}%`,
        protein_g:      `${result.macroGrams.proteinG}g`,
        carbs_pct:      `${Math.round(result.macros.carbs * 100)}%`,
        carbs_g:        `${result.macroGrams.carbsG}g`,
        fat_pct:        `${Math.round(result.macros.fat * 100)}%`,
        fat_g:          `${result.macroGrams.fatG}g`,
        products,
      },
    ]);

    // 3 — Subscribe to Macro Calculator list only if marketing checked
    if (raw.marketingAccepted) {
      fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=Wu9iyj`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2023-12-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Macro Calculator',
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: raw.email },
                },
              },
            },
            relationships: {
              list: {
                data: { type: 'list', id: 'S9jgLL' },
              },
            },
          },
        }),
      }).catch(() => {});
    }
  }

  /* ─────────────────────────────────────────────
     Error handling
     ───────────────────────────────────────────── */
  #showErrors(errors) {
    let firstEl = null;

    Object.entries(errors).forEach(([field, message]) => {
      const errorEl = this.querySelector(`[data-mc-error="${field}"]`);
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.removeAttribute('hidden');
      }

      // Mark input as invalid
      const inputEl = field === 'height'
        ? this.querySelector('[data-mc-input="height-ft"]')
        : this.querySelector(`[data-mc-input="${field}"], [name="${field}"]`);
      if (inputEl) {
        inputEl.setAttribute('aria-invalid', 'true');
        if (!firstEl) firstEl = inputEl;
      }
    });

    // Scroll to + focus first errored element
    if (firstEl) {
      firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      requestAnimationFrame(() => firstEl.focus());
    }
  }

  #clearAllErrors() {
    this.querySelectorAll('[data-mc-error]').forEach((el) => {
      el.textContent = '';
      el.setAttribute('hidden', '');
    });
    this.querySelectorAll('[aria-invalid]').forEach((el) =>
      el.removeAttribute('aria-invalid')
    );
  }

  #clearFieldError(field) {
    const el = this.querySelector(`[data-mc-error="${field}"]`);
    if (!el) return;
    el.textContent = '';
    el.setAttribute('hidden', '');
    const target = field === 'height'
      ? this.querySelector('[data-mc-input="height-ft"]')
      : this.querySelector(`[data-mc-input="${field}"], [name="${field}"]`);
    target?.removeAttribute('aria-invalid');
  }
}

if (!customElements.get('macro-calculator')) {
  customElements.define('macro-calculator', MacroCalculator);
}

/* ─────────────────────────────────────────────
   Swatch → card image swap
   Shared helper — looks up mediaId in the card's JSON media map
   and swaps the card's <img> src.
   ───────────────────────────────────────────── */
function mcSwapImage(card, mediaId) {
  if (!mediaId || mediaId === 'undefined') return;
  const mapEl = card.querySelector('[data-mc-media-map]');
  if (!mapEl) return;
  try {
    const map = JSON.parse(mapEl.textContent || '{}');
    const url = map[mediaId];
    if (url) {
      const img = card.querySelector('.mc-card-img-wrap img');
      if (img instanceof HTMLImageElement) {
        img.src = url;
        img.srcset = '';
      }
    }
  } catch {
    // malformed JSON — ignore
  }
}

/* On click/change — use capture phase because SwatchesVariantPickerComponent
   calls event.stopPropagation() during the bubble phase. */
document.addEventListener(
  'change',
  (e) => {
    const input = /** @type {HTMLElement|null} */ (e.target);
    if (!input || typeof input.matches !== 'function') return;
    if (!input.matches('.mc-product-card input[data-option-media-id]')) return;
    const card = input.closest('.mc-product-card');
    if (!card) return;
    mcSwapImage(card, /** @type {HTMLInputElement} */ (input).dataset.optionMediaId);
  },
  { capture: true }
);

/* On hover — preview the hovered swatch image.
   pointerenter doesn't bubble so capture: true is required. */
document.addEventListener(
  'pointerenter',
  (e) => {
    const label = /** @type {HTMLElement|null} */ (e.target);
    if (!label || typeof label.matches !== 'function') return;
    if (!label.matches('.mc-product-card .variant-option__button-label[data-media-id]')) return;
    const card = label.closest('.mc-product-card');
    if (!card) return;
    mcSwapImage(card, label.dataset.mediaId);
  },
  { capture: true }
);

/* On hover-out — restore the image of the currently-checked swatch. */
document.addEventListener(
  'pointerleave',
  (e) => {
    const label = /** @type {HTMLElement|null} */ (e.target);
    if (!label || typeof label.matches !== 'function') return;
    if (!label.matches('.mc-product-card .variant-option__button-label[data-media-id]')) return;
    const card = label.closest('.mc-product-card');
    if (!card) return;
    const checked = /** @type {HTMLInputElement|null} */ (
      card.querySelector('input[data-option-media-id]:checked')
    );
    if (checked) {
      mcSwapImage(card, checked.dataset.optionMediaId);
    }
  },
  { capture: true }
);
